import { Component, Input, Output, EventEmitter, ViewChild, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { SelectionModel } from '@angular/cdk/collections';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormatDateTimePipe } from '../../../core/pipe/custom-pipes';
import { BadgeColorPipe } from '../../../core/pipe/badge-color.pipe';
import { BadgeVehiclePipe } from '../../../core/pipe/badge-vehicle.pipe';
import { BadgeClientPipe } from '../../../core/pipe/badge-client.pipe';
import { BadgeDeliveryPipe } from '../../../core/pipe/badge-delivery.pipe';
import { Orders } from '../../../core/interfaces/orders';
import { OrdersSelectionService } from '../../../core/services/orders/orders-selection.service';

/**
 * OrdersTableComponent
 * Componente especializado para mostrar tabla de órdenes
 * Responsabilidad: Presentación de datos en tabla con 20 columnas
 * FASE B2: Completo con todas las columnas y lógica de negocio
 */
@Component({
  selector: 'app-orders-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatCheckboxModule,
    MatTooltipModule,
    TranslateModule,
    FormatDateTimePipe,
    BadgeColorPipe,
    BadgeVehiclePipe,
    BadgeClientPipe,
    BadgeDeliveryPipe
  ],
  templateUrl: './orders-table.component.html',
  styleUrls: ['./orders-table.component.css']
})
export class OrdersTableComponent implements OnInit, OnChanges, OnDestroy {
  
  // Inputs para datos y configuración
  @Input() orders: Orders[] = [];
  @Input() displayedColumns: string[] = [];
  @Input() typeOrders: string = '';
  @Input() loading: boolean = false;
  @Input() accountType: string | null = '';
  @Input() userRole: string = '';
  @Input() sortColumn: string = 'updated_at';
  @Input() sortDirection: string = 'asc';
  @Input() expandedRow: any = null;

  /** Ancho porcentual por columna — configurable desde el padre */
  @Input() columnWidths: { [key: string]: string } = {
    select: '3%',
    ring: '3%',
    info: '20%',
    client: '20%',
    vehicle: '20%',
    documents: '10%',
    telemarketing: '10%',
    vehicle_manufacturing_date: '10%',
    delivery_expected_date: '10%',
    updated_at: '10%',
    stage_updated_at: '10%',
    stage: '12%',
    actions: '5%',
    broker_sales_agent: '10%',
  };

  // Outputs para eventos
  @Output() rowClick = new EventEmitter<Orders>();
  @Output() actionClick = new EventEmitter<{ action: string; order: Orders }>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<string>();
  @Output() expandedRowChange = new EventEmitter<any>();
  @Output() deleteOrderClick = new EventEmitter<string>();
  @Output() editOrderClick = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<void>();
  @Output() documentClick = new EventEmitter<string>();
  @Output() openNotesClick = new EventEmitter<string>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();

  dataSource = new MatTableDataSource<Orders>([]);
  selection = new SelectionModel<any>(true, []);
  selectedReferences = new Set<string>();

  // Mapeo de columnas para sorting
  private readonly sortMap: { [key: string]: string } = {
    vehicle: 'vehicle',
    vendor: 'vendor',
    client: 'client',
    broker: 'broker',
    supplier: 'supplier',
    supplier_reference: 'supplier_reference',
    broker_reference: 'broker_reference',
    supplier_agent: 'supplier_agent',
    broker_agent: 'broker_agent',
    updated_at: 'updated_at',
    stage_updated_at: 'stage_updated_at',
    stage: 'stage',
    vehicle_manufacturing_date: 'vehicle_manufacturing_date',
    delivery_expected_date: 'delivery_expected_date',
    documents: 'documents'
  };

  constructor(public selectionService: OrdersSelectionService) {
  }

  ngOnInit(): void {
    this.updateDataSource();
  }

  ngOnChanges(): void {
    this.updateDataSource();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // Eventos
    if (this.paginator) {
      this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(event => {
        this.pageChange.emit(event);
      });
    }

    if (this.sort) {
      this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(event => {
        this.sortChange.emit(event.active);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDataSource(): void {
    this.dataSource.data = this.orders;
    
    // Restaurar selecciones
    if (this.orders.length > 0) {
      this.restoreSelections();
    }
  }

  // ============= MÉTODOS DE SELECCIÓN =============
  
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selectedReferences.clear();
      this.selectionService.clearSelection();
    } else {
      this.selection.clear();
      this.selectedReferences.clear();
      this.selectionService.clearSelection();
      this.dataSource.data.forEach((row) => {
        this.selection.select(row);
        this.selectedReferences.add(row.reference);
        this.selectionService.toggle(row.reference);
      });
    }
    this.selectionChange.emit();
  }

  toggleRowSelection(row: any): void {
    this.selection.toggle(row);
    this.selectionService.toggle(row.reference);
    if (this.selection.isSelected(row)) {
      this.selectedReferences.add(row.reference);
    } else {
      this.selectedReferences.delete(row.reference);
    }
    this.selectionChange.emit();
  }

  private restoreSelections(): void {
    this.selection.clear();
    this.dataSource.data.forEach((row) => {
      if (this.selectedReferences.has(row.reference)) {
        this.selection.select(row);
      }
    });
  }

  // ============= MÉTODOS DE INTERACCIÓN =============

  toggleRow(row: any): void {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) return;
    this.expandedRow = row;
    this.expandedRowChange.emit(row);
  }

  onRowClickHandler(order: Orders): void {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) return;
    this.toggleRow(order);
    this.rowClick.emit(order);
  }

  deleteOrder(reference: string, event: Event): void {
    event.stopPropagation();
    this.deleteOrderClick.emit(reference);
  }

  getOrder(reference: string, event: Event): void {
    event.stopPropagation();
    this.editOrderClick.emit(reference);
  }

  handleClickDocument(url: string, event: Event): void {
    event.stopPropagation();
    this.documentClick.emit(url);
  }

  // ============= MÉTODOS DE SORTING =============

  onSortChange2(columnName: string): void {
    this.sortChange.emit(columnName);
  }

  getDirection(): string {
    const directionMap: { [key: string]: string } = {
      asc: 'icon-arrow-up',
      desc: 'icon-arrow-down',
    };
    return directionMap[this.sortDirection] || 'icon-arrow-up';
  }

  shouldHideDeliveryBadge(): boolean {
    // Ocultar solo si es Broker Y está en vista de Postventa
    // Nota: Esta lógica requiere acceso a isAfterSalesView que está en OrdersRouteService
    // Por ahora devolvemos false y el componente padre maneja esta lógica
    return false; 
  }

  /**
   * Selecciona un color para el badge de documentos según el tipo
   * Migrado desde orders.component.ts (FASE B2)
   */
  pickAColor(type: string): string {
    const colorMap: Record<string, string> = {
      'identification': '#f0ab96',
      'organization_identification': '#f0ab96',
      'offer': '#f0ab96',

      'payroll': '#f0d3b1',
      'company_deed': '#f0d3b1',
      'offer_signed': '#f0d3b1',

      'employment_record': '#a7cce0',
      'ownership': '#a7cce0',
      'contract_signed': '#a7cce0',

      'bank_account_ownership': '#9daff0',
      'balance': '#9daff0',
      'guarantee_contract': '#9daff0',

      'receipt': '#c19fc2',
      'p_and_l': '#c19fc2',
      'guarantee_contract_signed': '#c19fc2',

      'pension_revaluation': '#f5f3a8',
      '200_organization_income_tax_return': '#f5f3a8',
      'delivery_certificate': '#f5f3a8',

      '037_census_declaration': '#ded9d5',
      '303_vat_return': '#ded9d5',
      'contract': '#ded9d5',

      '100_income_tax_return': '#8db99e',
      '390_vat_summary': '#8db99e',

      '130_quaterly_income_tax_return': '#4fc3c2',
      '131_quaterly_income_tax_return': '#a2d4df',
      'private': '#e6d8ff',
      'aux1': '#eaa3b5',
      'aux2': '#c2dbbe',
      'axu3': '#f9f6c1',
      'aux4': '#f7bd98'
    };

    return colorMap[type] || '#d0e5e8';
  }
}
